import { useNow } from '@web/hooks/useNow';
import { cn } from '@web/lib/cn';
import {
  fmtDuration,
  WAIT_LATE_MS,
  WAIT_WARN_MS,
  windowExpiresAt,
  windowStatus,
} from '@web/lib/time';

/**
 * Everything that ticks.
 *
 * Three rules these share, and all three exist because this text updates once a
 * second under someone's eyes for hours:
 *  - Monospace with tabular figures and a fixed column width, so a digit
 *    changing never re-flows the row.
 *  - The ticking span is `aria-hidden` and the cell carries a label that only
 *    changes when a THRESHOLD is crossed. A live timer inside a live region
 *    announces itself every second and makes the screen reader useless.
 *  - No motion. A colour change at five and fifteen minutes, and nothing else.
 */

function elapsed(since: string | null, now: number): number | null {
  if (since === null) return null;
  const at = new Date(since).getTime();
  return Number.isNaN(at) ? null : Math.max(0, now - at);
}

/** How long since anyone said anything. Neutral: silence is not yet a problem. */
export function IdleTimer({ since, className }: { since: string | null; className?: string }) {
  const now = useNow();
  const ms = elapsed(since, now);

  if (ms === null) {
    return (
      <span className={cn('font-mono text-xs text-ink-faint tabular-nums', className)}>—</span>
    );
  }

  return (
    <span className={cn('font-mono text-xs text-ink-dim tabular-nums', className)} aria-hidden>
      {fmtDuration(ms)}
    </span>
  );
}

export type WaitBand = 'ok' | 'warn' | 'late';

export function waitBand(ms: number): WaitBand {
  if (ms >= WAIT_LATE_MS) return 'late';
  if (ms >= WAIT_WARN_MS) return 'warn';
  return 'ok';
}

const WAIT_LABEL: Record<WaitBand, string> = {
  ok: 'waiting under five minutes',
  warn: 'waiting over five minutes',
  late: 'waiting over fifteen minutes',
};

/**
 * How long someone has been in the queue. This is the number staff are judged
 * on, so it is the one timer that changes colour — amber past five minutes, the
 * reserved signal hue past fifteen, and bold at both, so the escalation is
 * legible without colour at all.
 */
export function WaitTimer({
  since,
  className,
  size = 'compact',
}: {
  since: string | null;
  className?: string;
  size?: 'compact' | 'large';
}) {
  const now = useNow();
  const ms = elapsed(since, now);

  if (ms === null) {
    return <span className={cn('font-mono text-ink-faint tabular-nums', className)}>—</span>;
  }

  const band = waitBand(ms);
  return (
    <span
      className={cn(
        'font-mono tabular-nums',
        size === 'large' ? 'text-lg' : 'text-xs',
        band === 'ok' && 'text-ink-dim',
        band === 'warn' && 'font-semibold text-warm',
        band === 'late' && 'font-semibold text-signal',
        className,
      )}
      // The label is derived from the band, so it is re-announced when the row
      // escalates and never when a second ticks over.
      aria-label={WAIT_LABEL[band]}
    >
      <span aria-hidden>{fmtDuration(ms)}</span>
    </span>
  );
}

const WINDOW_LABEL = {
  open: 'service window open',
  closing: 'service window closing',
  expired: 'service window expired — templates only',
  'never-opened': 'no inbound message yet',
} as const;

/**
 * WhatsApp's 24-hour service window, counted from the candidate's last inbound
 * message. When it closes, free-form replies stop being deliverable and the
 * composer has to swap to approved templates — so this is not a decoration, it
 * is the countdown to a different set of rules.
 */
export function WindowCountdown({
  lastInboundAt,
  className,
  size = 'compact',
}: {
  lastInboundAt: string | null;
  className?: string;
  size?: 'compact' | 'full';
}) {
  const now = useNow();
  const expiresAt = windowExpiresAt(lastInboundAt);
  const status = windowStatus(expiresAt, now);

  const text =
    status === 'expired'
      ? 'CLOSED'
      : status === 'never-opened'
        ? '—'
        : fmtDuration((expiresAt ?? 0) - now);

  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1.5 font-mono tabular-nums',
        size === 'full' ? 'text-xs' : 'text-[11px]',
        status === 'open' && 'text-ink-dim',
        status === 'closing' && 'font-semibold text-warm',
        status === 'expired' && 'font-semibold text-signal',
        status === 'never-opened' && 'text-ink-faint',
        className,
      )}
      aria-label={WINDOW_LABEL[status]}
    >
      {size === 'full' && (
        <span aria-hidden className="tracking-[0.08em] text-ink-faint">
          WINDOW
        </span>
      )}
      <span aria-hidden>{text}</span>
    </span>
  );
}
